import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronRight,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import DiffHighlighter from './DiffHighlighter';
import InlineErrorDisplay from './InlineErrorDisplay';

const formatJson = (data) => {
  return JSON.stringify(data, null, 2);
};

const getRuleTypeLabel = (ruleType) => {
  const types = {
    'STRING_MANIPULATION': 'String Manipulation',
    'CONDITIONAL': 'Conditional Logic',
    'LOOKUP': 'Reference Data Lookup'
  };
  return types[ruleType] || ruleType;
};

const getStatusIcon = (success, requiresReview) => {
  if (!success) {
    return <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#dc3545' }} />;
  }
  if (requiresReview) {
    return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107' }} />;
  }
  return <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745' }} />;
};

const TestResultsDisplay = ({ testResults, isExpanded, onToggleSection }) => {
  if (!testResults) return null;

  return (
    <div className="mt-4">
      <h4 className="mb-3">Test Results</h4>

      {/* Summary */}
      <div className="alert alert-info mb-4">
        <div className="row">
          <div className="col-md-3">
            <strong>Total Execution Time:</strong><br />
            {testResults.totalExecutionTimeMs.toFixed(2)} ms
          </div>
          <div className="col-md-3">
            <strong>Rules Executed:</strong><br />
            {testResults.ruleResults.length}
          </div>
          <div className="col-md-3">
            <strong>Successful:</strong><br />
            {testResults.ruleResults.filter(r => r.success).length}
          </div>
          <div className="col-md-3">
            <strong>Failed:</strong><br />
            {testResults.ruleResults.filter(r => !r.success).length}
          </div>
        </div>
      </div>

      {/* Input Data */}
      <div className="card mb-3">
        <div 
          className="card-header" 
          style={{ cursor: 'pointer', backgroundColor: '#e9ecef' }}
          onClick={() => onToggleSection('inputData')}
        >
          <FontAwesomeIcon 
            icon={isExpanded('inputData') ? faChevronDown : faChevronRight} 
            className="me-2"
          />
          <strong>Input Data (Original Extracted Data)</strong>
        </div>
        {isExpanded('inputData') && (
          <div className="card-body">
            <SyntaxHighlighter 
              language="json" 
              style={docco}
              customStyle={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                fontSize: '0.9em'
              }}
            >
              {formatJson(testResults.inputData)}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Rule Results */}
      <h5 className="mb-3">Rule Execution Steps</h5>
      {testResults.ruleResults.map((result, index) => (
        <div key={result.ruleId} className="card mb-3">
          <div 
            className="card-header" 
            style={{ 
              cursor: 'pointer',
              backgroundColor: result.success ? '#d4edda' : '#f8d7da'
            }}
            onClick={() => onToggleSection('rules', result.ruleId)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <FontAwesomeIcon 
                  icon={isExpanded('rules', result.ruleId) ? faChevronDown : faChevronRight} 
                  className="me-2"
                />
                {getStatusIcon(result.success, result.requiresReview)}
                <strong className="ms-2">
                  Step {index + 1}: {result.ruleName}
                </strong>
                <span className="badge bg-secondary ms-2">{getRuleTypeLabel(result.ruleType)}</span>
                {result.requiresReview && (
                  <span className="badge bg-warning ms-2">Requires Review</span>
                )}
              </div>
              <div>
                <small className="text-muted">{result.executionTimeMs.toFixed(2)} ms</small>
              </div>
            </div>
          </div>
          {isExpanded('rules', result.ruleId) && (
            <div className="card-body">
              {!result.success && result.errorMessage && (
                (result.errorField || result.errorParameter) ? (
                  <InlineErrorDisplay
                    errorMessage={result.errorMessage}
                    errorField={result.errorField}
                    errorParameter={result.errorParameter}
                    ruleName={result.ruleName}
                    ruleType={getRuleTypeLabel(result.ruleType)}
                    executionOrder={index + 1}
                  />
                ) : (
                  <div className="alert alert-danger mb-3">
                    <strong>Error:</strong>
                    <pre style={{ 
                      marginTop: '10px', 
                      marginBottom: 0,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85em'
                    }}>
                      {result.errorMessage}
                    </pre>
                  </div>
                )
              )}

              {result.requiresReview && result.reviewReason && (
                <div className="alert alert-warning mb-3">
                  <strong>Review Required:</strong> {result.reviewReason}
                  {result.reviewContext && Object.keys(result.reviewContext).length > 0 && (
                    <div className="mt-2">
                      <strong>Context:</strong>
                      <SyntaxHighlighter 
                        language="json" 
                        style={docco}
                        customStyle={{ 
                          marginTop: '10px',
                          fontSize: '0.85em'
                        }}
                      >
                        {formatJson(result.reviewContext)}
                      </SyntaxHighlighter>
                    </div>
                  )}
                </div>
              )}

              <DiffHighlighter inputData={result.inputData} outputData={result.outputData} />
            </div>
          )}
        </div>
      ))}

      {/* Final Output */}
      <div className="card mb-3">
        <div 
          className="card-header" 
          style={{ cursor: 'pointer', backgroundColor: '#d1ecf1' }}
          onClick={() => onToggleSection('finalOutput')}
        >
          <FontAwesomeIcon 
            icon={isExpanded('finalOutput') ? faChevronDown : faChevronRight} 
            className="me-2"
          />
          <strong>Final Output (After All Rules)</strong>
        </div>
        {isExpanded('finalOutput') && (
          <div className="card-body">
            <SyntaxHighlighter 
              language="json" 
              style={docco}
              customStyle={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                fontSize: '0.9em'
              }}
            >
              {formatJson(testResults.finalOutput)}
            </SyntaxHighlighter>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResultsDisplay;
